import React, { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import noop from 'lodash/noop';
import { createContext } from 'use-context-selector';
import { MEDIA_EVENT_LISTENER } from '../components/video/constants/video-events';
import { getDefaultPlaySpeed } from './utils/mediaState';
import { type MediaStateContextT } from './media-state-provider';
type PropsT = {
  children?: ReactNode;
  frameRate?: number;
};
type MediaStateContextTLite = MediaStateContextT & {
  currentFrame: number;
  resetMediaState: () => void;
  maxFrames: number;
};

//@ts-ignore
export const MediaStateContextLite = createContext<MediaStateContextTLite>({
  isPlaying: false,
  onPlaySpeedChange: noop,
  onPlayStatusChange: noop,
  updateFrameLoadStatus: noop,
  onMediaMetadataLoad: noop,
  mediaFrameLoadStatus: [],
  updateToLastFrame: noop,
  isFramesLoading: false,
  mediaVolume: 1,
  onMediaVolumeChange: noop,
  onSeekToFrame: noop,
  resetMediaState: noop,
  currentFrame: 0,
  maxFrames: 0
});

/**
 * Lightweight MediaStateProvider for context videos (view-only).
 * Only provides essential playback controls: play, pause, speed, and volume.
 * No frame tracking, no frame decoding, no frame synchronization.
 */
export const MediaStateProviderLite = ({
  children,
  frameRate
}: PropsT) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [playSpeed, setPlaySpeed] = useState<number>(() => getDefaultPlaySpeed() ?? 1);
  const [mediaVolume, setMediaVolume] = useState<number>(1);
  const [mediaDuration, setMediaDuration] = useState<number>(0);
  const [mediaElRef, setMediaElRef] = useState<HTMLVideoElement | null>(null);
  const [maxFrames, setMaxFrames] = useState<number>(1);
  const [currentFrame, setCurrentFrame] = useState<number>(0);
  const onPlayStatusChange = useCallback((playbackState: boolean) => {
    setIsPlaying(playbackState);
  }, []);
  const onPlaySpeedChange = useCallback((updatedPlaySpeed: number) => {
    setPlaySpeed(updatedPlaySpeed);
  }, []);
  const onMediaVolumeChange = useCallback((volume: number) => {
    setMediaVolume(volume);
  }, []);
  const onMediaMetadataLoad = useCallback((duration: number, mediaEl: HTMLVideoElement | null) => {
    setMediaDuration(duration);
    setMediaElRef(mediaEl);
    setMaxFrames(Math.floor(duration * (frameRate ?? 30)));
  }, [frameRate]);
  const resetMediaState = useCallback(() => {
    setIsPlaying(false);
    setPlaySpeed(() => getDefaultPlaySpeed() ?? 1);
    setMediaVolume(1);
    setMediaDuration(0);
    setMediaElRef(null);
    setMaxFrames(1);
    setCurrentFrame(0);
  }, []);
  useEffect(() => {
    const handleTimeUpdate = () => {
      if (!mediaElRef) return;
      const currentTime = mediaElRef.currentTime;
      const effectiveFrameRate = frameRate ?? 30;
      // Calculate frame based on current time - this automatically accounts for playback speed
      const frame = Math.floor(currentTime * effectiveFrameRate);
      setCurrentFrame(frame);
    };
    const onEnd = () => {
      setIsPlaying(false);
      setCurrentFrame(0);
    };
    mediaElRef?.addEventListener(MEDIA_EVENT_LISTENER.TIME_UPDATE, handleTimeUpdate);
    mediaElRef?.addEventListener(MEDIA_EVENT_LISTENER.ENDED, onEnd);
    return () => {
      mediaElRef?.removeEventListener(MEDIA_EVENT_LISTENER.TIME_UPDATE, handleTimeUpdate);
      mediaElRef?.removeEventListener(MEDIA_EVENT_LISTENER.ENDED, onEnd);
    };
  }, [mediaElRef, frameRate, resetMediaState]);
  const onSeekToFrame = useCallback((frame: number) => {
    if (isPlaying) {
      setIsPlaying(false);
    }
    setCurrentFrame(frame);
  }, [isPlaying]);
  const mediaStateValue: MediaStateContextTLite = useMemo(() => ({
    isPlaying,
    playSpeed,
    mediaVolume,
    mediaDuration,
    onPlayStatusChange,
    onPlaySpeedChange,
    onMediaVolumeChange,
    onMediaMetadataLoad,
    currentFrame,
    // Seek functionality for context videos
    onSeekToFrame,
    // Other required properties
    mediaFrameLoadStatus: [],
    updateFrameLoadStatus: noop,
    mediaFrameRate: frameRate ?? 30,
    frameRate: frameRate ?? 30,
    onVideoLoadStart: noop,
    resetMediaState,
    maxFrames,
    mediaElement: mediaElRef
  }), [isPlaying, playSpeed, mediaVolume, mediaDuration, currentFrame, frameRate, maxFrames, mediaElRef, onPlayStatusChange, onPlaySpeedChange, onMediaVolumeChange, onMediaMetadataLoad, onSeekToFrame, resetMediaState]);
  return <MediaStateContextLite.Provider value={mediaStateValue}>
      {children}
    </MediaStateContextLite.Provider>;
};